import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import styles from './styles/ListasPrecios.module.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type TabKey = 'listas' | 'promociones' | 'presupuestos' | 'cupones';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'listas', label: 'Listas de Precios' },
    { key: 'promociones', label: 'Promociones' },
    { key: 'presupuestos', label: 'Presupuestos' },
    { key: 'cupones', label: 'Cupones' },
];

interface ListaPrecios {
    id: number;
    nombre: string;
    tipo: string;
    moneda: string;
    factor: number;
    productos: number;
    valida_desde: string | null;
    valida_hasta: string | null;
    activa: boolean;
}

interface Promocion {
    idProductos: number;
    NombresProductos: string;
    Precio: number;
    activo?: boolean | number;
}

const MONEDAS: Record<string, string> = { ARS: 'ARS ($)', USD: 'USD ($)' };

/** Vacio = sin fecha de fin, que en la grilla se lee como "Permanente". */
const vigencia = (valor: string | null) => valor || 'Permanente';

const ListasPrecios: React.FC = () => {
    const [tab, setTab] = useState<TabKey>('listas');
    const [listas, setListas] = useState<ListaPrecios[]>([]);
    const [promos, setPromos] = useState<Promocion[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editando, setEditando] = useState<ListaPrecios | null>(null);
    const [creando, setCreando] = useState(false);

    const cargar = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API}/admin/listas-precios`);
            const data = await res.json();
            setListas(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error cargando listas', err);
            setError('No se pudieron cargar las listas de precios.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    // Las promociones ya existen como productos marcados: se reusan para el
    // resumen en vez de inventar una entidad nueva.
    useEffect(() => {
        fetch(`${API}/admin/promociones`)
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setPromos(data); })
            .catch(() => { /* el resumen queda vacio */ });
    }, []);

    const guardar = async (lista: Partial<ListaPrecios> & { id?: number }) => {
        const esNueva = !lista.id;
        const url = esNueva
            ? `${API}/admin/listas-precios`
            : `${API}/admin/listas-precios/${lista.id}`;
        try {
            const res = await fetch(url, {
                method: esNueva ? 'POST' : 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(lista),
            });
            if (!res.ok) throw new Error('no ok');
            setEditando(null);
            setCreando(false);
            cargar();
        } catch (err) {
            console.error('Error guardando lista', err);
            setError('No se pudo guardar la lista.');
        }
    };

    const activas = useMemo(() => listas.filter(l => l.activa).length, [listas]);

    return (
        <div className={styles.lp}>
            <header className={styles.head}>
                <div>
                    <h2 className={styles.headTitle}>Listas de Precios y Promociones</h2>
                    <p className={styles.headSub}>
                        Gestión de políticas de precios para venta minorista, mayorista,
                        combos promocionales y cupones.
                    </p>
                </div>
                <button type="button" className={styles.primaryBtn} onClick={() => setCreando(true)}>
                    <FaPlus /> Nueva Lista de Precios
                </button>
            </header>

            <div className={styles.tabs} role="tablist">
                {TABS.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        className={`${styles.tab} ${tab === t.key ? styles.tabActive : ''}`}
                        onClick={() => setTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {error && <div className={styles.errorBar}>{error}</div>}

            {tab === 'listas' && (
                <>
                    <section className={styles.panel}>
                        <header className={styles.panelHead}>
                            <h3>Listas de Precios Vigentes</h3>
                            <span className={styles.panelMeta}>
                                {activas} de {listas.length} activas
                            </span>
                        </header>

                        <div className={styles.tableWrap}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Nombre Lista</th>
                                        <th>Tipo</th>
                                        <th>Moneda</th>
                                        <th className={styles.num}>Productos</th>
                                        <th>Válida Desde</th>
                                        <th>Válida Hasta</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr><td colSpan={8} className={styles.empty}>Cargando listas…</td></tr>
                                    )}
                                    {!loading && listas.length === 0 && (
                                        <tr><td colSpan={8} className={styles.empty}>
                                            No hay listas de precios cargadas.
                                        </td></tr>
                                    )}
                                    {!loading && listas.map(l => (
                                        <tr key={l.id}>
                                            <td className={styles.strong}>{l.nombre}</td>
                                            <td>{l.tipo}</td>
                                            <td>{MONEDAS[l.moneda] || l.moneda}</td>
                                            <td className={styles.num}>
                                                {l.productos.toLocaleString('es-AR')} ítems
                                            </td>
                                            <td className={styles.nowrap}>{l.valida_desde || '—'}</td>
                                            <td className={styles.nowrap}>{vigencia(l.valida_hasta)}</td>
                                            <td>
                                                <span className={`${styles.badge} ${l.activa ? styles.badgeOn : styles.badgeOff}`}>
                                                    {l.activa ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className={styles.linkBtn}
                                                    onClick={() => setEditando(l)}
                                                >
                                                    Editar
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section className={styles.panel}>
                        <header className={styles.panelHead}>
                            <h3>Resumen de Promociones Activas</h3>
                            <span className={styles.panelMeta}>{promos.length} promociones</span>
                        </header>

                        {promos.length === 0 ? (
                            <p className={styles.empty}>No hay promociones cargadas.</p>
                        ) : (
                            <div className={styles.promoGrid}>
                                {promos.slice(0, 6).map(p => (
                                    <article key={p.idProductos} className={styles.promoCard}>
                                        <div className={styles.promoTop}>
                                            <span className={styles.promoTag}>Combo</span>
                                            <span className={`${styles.badge} ${styles.badgeOn}`}>Activa</span>
                                        </div>
                                        <strong className={styles.promoName}>{p.NombresProductos}</strong>
                                        <span className={styles.promoMeta}>
                                            Precio del combo: ${Math.round(p.Precio).toLocaleString('es-AR')}
                                        </span>
                                    </article>
                                ))}
                            </div>
                        )}
                    </section>
                </>
            )}

            {tab !== 'listas' && (
                <div className={styles.placeholder}>
                    <h3>{TABS.find(t => t.key === tab)?.label}</h3>
                    <p>
                        {tab === 'promociones'
                            ? 'Las promociones se administran desde la pantalla Promociones del panel anterior.'
                            : tab === 'presupuestos'
                                ? 'Emisión de presupuestos con validez y conversión a venta.'
                                : 'Cupones de descuento por porcentaje o importe fijo.'}
                    </p>
                </div>
            )}

            {(creando || editando) && (
                <ListaModal
                    lista={editando}
                    onClose={() => { setCreando(false); setEditando(null); }}
                    onSave={guardar}
                />
            )}
        </div>
    );
};

/* --------------------------------------------------------------- modal lista */

interface ListaModalProps {
    lista: ListaPrecios | null;
    onClose: () => void;
    onSave: (lista: Partial<ListaPrecios> & { id?: number }) => void;
}

/** 'DD/MM/YYYY' -> 'YYYY-MM-DD', que es lo que espera <input type="date">. */
const aISO = (valor: string | null) => {
    if (!valor) return '';
    const [d, m, y] = valor.split('/');
    return d && m && y ? `${y}-${m}-${d}` : '';
};

const ListaModal: React.FC<ListaModalProps> = ({ lista, onClose, onSave }) => {
    const [nombre, setNombre] = useState(lista?.nombre ?? '');
    const [tipo, setTipo] = useState(lista?.tipo ?? 'Minorista');
    const [moneda, setMoneda] = useState(lista?.moneda ?? 'ARS');
    const [factor, setFactor] = useState(String(lista?.factor ?? 1));
    const [desde, setDesde] = useState(aISO(lista?.valida_desde ?? null));
    const [hasta, setHasta] = useState(aISO(lista?.valida_hasta ?? null));
    const [activa, setActiva] = useState(lista?.activa ?? true);

    const guardar = () => {
        if (!nombre.trim()) return;
        onSave({
            id: lista?.id,
            nombre: nombre.trim(),
            tipo,
            moneda,
            factor: Number(factor) || 1,
            valida_desde: desde || null,
            valida_hasta: hasta || null,
            activa,
        });
    };

    // El factor se muestra tambien como porcentaje: es mas legible que 0.79.
    const pct = Math.round((1 - (Number(factor) || 1)) * 100);

    return (
        <div
            className={styles.overlay}
            role="presentation"
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Lista de precios">
                <div className={styles.modalHead}>
                    <h3>{lista ? 'Editar lista' : 'Nueva lista de precios'}</h3>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Cerrar">×</button>
                </div>

                <div className={styles.modalBody}>
                    <label className={styles.field}>
                        <span>Nombre de la lista</span>
                        <input value={nombre} onChange={e => setNombre(e.target.value)} />
                    </label>

                    <div className={styles.fieldRow}>
                        <label className={styles.field}>
                            <span>Tipo</span>
                            <select value={tipo} onChange={e => setTipo(e.target.value)}>
                                <option>Minorista</option>
                                <option>Mayorista</option>
                                <option>Especial</option>
                            </select>
                        </label>
                        <label className={styles.field}>
                            <span>Moneda</span>
                            <select value={moneda} onChange={e => setMoneda(e.target.value)}>
                                <option value="ARS">ARS ($)</option>
                                <option value="USD">USD ($)</option>
                            </select>
                        </label>
                    </div>

                    <label className={styles.field}>
                        <span>
                            Coeficiente sobre el precio de catálogo
                            {pct !== 0 && (
                                <em className={styles.fieldHint}>
                                    {pct > 0 ? ` (${pct}% de descuento)` : ` (${-pct}% de recargo)`}
                                </em>
                            )}
                        </span>
                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={factor}
                            onChange={e => setFactor(e.target.value)}
                        />
                    </label>

                    <div className={styles.fieldRow}>
                        <label className={styles.field}>
                            <span>Válida desde</span>
                            <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
                        </label>
                        <label className={styles.field}>
                            <span>Válida hasta (vacío = permanente)</span>
                            <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
                        </label>
                    </div>

                    <label className={styles.checkField}>
                        <input type="checkbox" checked={activa} onChange={e => setActiva(e.target.checked)} />
                        Lista activa
                    </label>
                </div>

                <div className={styles.modalFoot}>
                    <button type="button" className={styles.ghostBtn} onClick={onClose}>Cancelar</button>
                    <button type="button" className={styles.primaryBtn} onClick={guardar}>Guardar</button>
                </div>
            </div>
        </div>
    );
};

export default ListasPrecios;
